import {useGetBasicDataItems} from '@/modules/basic-data/api'
import {mapBasicDataItemsToSelectItems} from '@/modules/basic-data/functions'
import {useGetPatient} from '@/modules/patients/api'
import {useGetPatientLatestGfrLab} from '@/modules/patients/api/use-get-patient-latest-gfr-lab'
import {useGetPatientRecalculationDoses} from '@/modules/patients/api/use-get-patient-recalculation-doses'
import {confirmCharacteristicsUpdate} from '@/modules/patients/components/dialogs/confirm-characteristics-update-dialog'
import {openPatientInformationDialog} from '@/modules/patients/components/patient-details/about/util'
import {useGetSettings} from '@/modules/settings/api'
import {AlertBox} from '@/shared/components/ui/alert-box'
import {bsaFormulasOptions, ibwFormulasOptions} from '@/shared/constants'
import {useZero} from '@/shared/integrations/zero/use-zero'
import {Button, ButtonGroup, HStack, VStack} from '@chakra-ui/react'
import {BSAFormula, DiabetesSchema, IBWFormula} from '@cypro/engine/database/schema'
import {BasicDataResources, Patient, Settings} from '@cypro/engine/schema'
import {calculateBodyMeasures, getDosingWeight, isAibwMissing, previewCharacteristicsDoseChanges} from '@cypro/engine/utils'
import {dialog, DialogBody, DialogFooter, DialogHeader, Tooltip} from '@cypro/ui/components'
import {focusFirstErrorField, useAppForm} from '@cypro/ui/integrations/form'
import {toaster} from '@cypro/ui/toaster'
import {useStore} from '@tanstack/react-form'
import {useTranslate, T} from '@tolgee/react'
import * as z from 'zod'

function onClose(result: boolean) {
  dialog.close('patient-characteristics', result)
}

const CharacteristicsSchema = z
  .object({
    height: z.number().max(300).positive(),
    weight: z.number().max(700).positive(),
    bsa: z.number(),
    aibw: z.number(),
    diabetes: DiabetesSchema.or(z.undefined()),
    useAibw: z.boolean(),
    smoker: z.boolean(),
    alcoholic: z.boolean(),
    diagnosisId: z.uuid().or(z.undefined()),
    bsaFormula: BSAFormula,
    aibwFormula: IBWFormula,
  })
  .superRefine((data, ctx) => {
    // weight-based doses use the AIBW when it is switched on, so it must exist
    if (isAibwMissing(data)) {
      ctx.addIssue({code: 'custom', path: ['aibw'], message: 'characteristics.aibw_required'})
    }
  })

export function CharacteristicsDialog({patientId}: {patientId: string}) {
  const {t} = useTranslate('common')
  const zero = useZero()

  const [patient] = useGetPatient(patientId)
  const [doses] = useGetPatientRecalculationDoses(patientId)
  const [gfr] = useGetPatientLatestGfrLab(patientId)

  const {settings} = useGetSettings()

  const {items: diagnoses} = useGetBasicDataItems({resource: BasicDataResources.diagnoses})
  const diagnosisSelectItems = mapBasicDataItemsToSelectItems(diagnoses, {includeCode: false})

  const diabetesSelectItems = [
    {value: 'type1', label: t('characteristics.diabetes.type1')},
    {value: 'type2', label: t('characteristics.diabetes.type2')},
    {value: 'none', label: t('characteristics.diabetes.none')},
  ]

  const form = useAppForm({
    defaultValues: getFormDefaultValues(patient, settings),
    validators: {
      onSubmit: CharacteristicsSchema,
    },
    listeners: {
      onChange: ({fieldApi}) => {
        const {name} = fieldApi
        // we're watching height, weight, the formulas and useAibw - if they change we need to recalculate BSA and AIBW

        if (!['height', 'weight', 'bsaFormula', 'aibwFormula', 'useAibw'].includes(name)) return

        const height = name === 'height' ? (fieldApi.state.value as number) : form.getFieldValue('height')
        const weight = name === 'weight' ? (fieldApi.state.value as number) : form.getFieldValue('weight')
        const bsaFormula = name === 'bsaFormula' ? fieldApi.state.value : form.getFieldValue('bsaFormula')
        const aibwFormula = name === 'aibwFormula' ? fieldApi.state.value : form.getFieldValue('aibwFormula')
        const useAibw = name === 'useAibw' ? (fieldApi.state.value as boolean) : form.getFieldValue('useAibw')

        const {aibw, bsa} = calculateBodyMeasures({height, weight, sex: patient.sex, useAibw, bsaFormula, aibwFormula})
        form.setFieldValue('bsa', bsa)
        form.setFieldValue('aibw', aibw)
      },
    },
    async onSubmit({value}) {
      const characteristics = patient?.characteristics?.[0]
      const {recalculated, kept} = previewCharacteristicsDoseChanges(
        doses,
        {
          height: characteristics?.height,
          weight: getDosingWeight(characteristics),
          bsa: characteristics?.bsa,
          gfr: gfr?.gfr,
        },
        {height: value.height, weight: getDosingWeight(value), bsa: value.bsa, gfr: gfr?.gfr}
      )
      const confirmed = await confirmCharacteristicsUpdate({
        weight: {old: characteristics?.weight, new: value.weight},
        bsa: {old: characteristics?.bsa, new: value.bsa},
        recalculated,
        kept,
      })
      if (!confirmed) return

      return zero.attemptMutation(
        zero.mutators.patients.addCharacteristics({
          patientId,
          ...value,
        }),
        async mutation => {
          const data = await mutation.server
          if (!data) return
          // sometimes the overlay isn't synced with the state of the dialog
          setTimeout(() => onClose(true), 0)
          toaster.success({
            title: t('patient.characteristics.saved.title'),
            description: t('patient.characteristics.saved.desc'),
          })
        }
      )
    },
    onSubmitInvalid() {
      focusFirstErrorField()
    },
  })

  const isDefaultValue = useStore(form.store, state => state.isDefaultValue)
  const useAibw = useStore(form.store, state => state.values.useAibw)

  if (!patient) return null

  return (
    <form
      onSubmit={e => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <form.AppForm>
        <DialogHeader>{t('patient.characteristics')}</DialogHeader>

        <DialogBody minHeight="none">
          <VStack gap="5" width="100%">
            {!patient.sex && (
              <AlertBox
                as="button"
                onClick={() => patient && openPatientInformationDialog(patient)}
                message={<T keyName="characteristics.sex_missing" ns="common" params={{b: <b />}} />}
              />
            )}

            <form.AppField name="height">
              {field => <field.NumberInput label={t('characteristics.height')} placeholder="e.g. 170" />}
            </form.AppField>

            <form.AppField name="weight">
              {field => <field.NumberInput label={t('characteristics.weight')} placeholder="e.g. 70" />}
            </form.AppField>

            <HStack width="full" justifyContent="space-between">
              <form.AppField name="bsa">
                {field => <field.TextField disabled label={t('characteristics.bsa')} />}
              </form.AppField>

              <form.AppField name="bsaFormula" mode="value">
                {field => (
                  <field.Select
                    label={t('characteristics.bsaFormula')}
                    placeholder={t('eg', {value: bsaFormulasOptions?.[0]?.label})}
                    items={bsaFormulasOptions}
                    contentProps={{zIndex: 2000}}
                  />
                )}
              </form.AppField>
            </HStack>

            <HStack width="full" justifyContent="space-between">
              <form.AppField name="aibw">
                {field => <field.TextField disabled label={t('characteristics.aibw')} />}
              </form.AppField>

              <form.AppField name="aibwFormula" mode="value">
                {field => (
                  <Tooltip content={useAibw ? null : t('field_is_disabled_because_aibw_is_disabled')}>
                    <field.Select
                      label={t('characteristics.aibwFormula')}
                      placeholder={t('eg', {value: ibwFormulasOptions?.[0]?.label})}
                      items={ibwFormulasOptions}
                      contentProps={{zIndex: 2000}}
                      disabled={!useAibw}
                    />
                  </Tooltip>
                )}
              </form.AppField>
            </HStack>

            <form.AppField name="diagnosisId" mode="value">
              {field => (
                <field.Select
                  label={t('characteristics.diagnosis')}
                  placeholder={t('eg', {value: diagnosisSelectItems?.[0]?.label})}
                  items={diagnosisSelectItems}
                  contentProps={{zIndex: 2000}}
                />
              )}
            </form.AppField>

            <form.AppField name="diabetes" mode="value">
              {field => (
                <field.Select
                  label={t('characteristics.diabetes')}
                  placeholder={t('eg', {value: diabetesSelectItems?.[0]?.label})}
                  items={diabetesSelectItems}
                  contentProps={{zIndex: 2000}}
                />
              )}
            </form.AppField>

            <form.AppField name="useAibw">
              {field => <field.Switch label={t('characteristics.useAibw')} />}
            </form.AppField>

            <form.AppField name="smoker">{field => <field.Switch label={t('characteristics.smoker')} />}</form.AppField>

            <form.AppField name="alcoholic">
              {field => <field.Switch label={t('characteristics.alcoholic')} />}
            </form.AppField>
          </VStack>
        </DialogBody>

        <DialogFooter>
          <ButtonGroup size="md" colorPalette="cyan" width="100%" justifyContent="flex-end">
            <Button variant="outline" colorPalette="gray" onClick={() => onClose(false)}>
              {t('cancel')}
            </Button>
            <form.SubmitButton disabled={isDefaultValue || !patient.sex}>{t('save')}</form.SubmitButton>
          </ButtonGroup>
        </DialogFooter>
      </form.AppForm>
    </form>
  )
}

function getFormDefaultValues(patient: Patient | undefined, settings: Settings | undefined) {
  const characteristics = patient?.characteristics?.[0]
  const height = characteristics?.height ?? 0
  const weight = characteristics?.weight ?? 0
  const defaultBSAFormula = settings?.bsaFormula || 'DU_BOIS'
  const defaultAIBWFormula = settings?.ibwFormula || 'DEVINE'
  // migrated characteristics have no formulas; calculate with the ones the formula fields show
  const bsaFormula = characteristics?.bsaFormula ?? defaultBSAFormula
  const aibwFormula = characteristics?.aibwFormula ?? defaultAIBWFormula
  const useAibw = Boolean(characteristics?.useAibw)

  return {
    height,
    weight,
    ...calculateBodyMeasures({height, weight, sex: patient?.sex, useAibw, bsaFormula, aibwFormula}),
    diabetes: characteristics?.diabetes || undefined,
    useAibw,
    smoker: Boolean(characteristics?.smoker),
    alcoholic: Boolean(characteristics?.alcoholic),
    diagnosisId: characteristics?.diagnosisId || undefined,
    bsaFormula,
    aibwFormula,
  }
}
